'use client';

import { useState, useCallback, memo, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLenis } from 'lenis/react';
import styles from './Navbar.module.css';
import { Button } from '../ui/Button';
import { useAnchorScroll } from '../../hooks/useAnchorScroll';
import KoalendarWidget from '../KoalendarWidget';

// Memoized NavLink component to prevent unnecessary re-renders
const NavLink = memo(({ link, isActive, onClick, className }) => {
    return (
        <Link
            href={link.href}
            className={`${className} ${isActive ? styles.active : ''}`}
            onClick={(e) => onClick(e, link.href)}
        >
            {link.name}
        </Link>
    );
});

NavLink.displayName = 'NavLink';

export const Navbar = () => {
    const handleAnchorClick = useAnchorScroll();
    const headerRef = useRef(null);
    const scrolledRef = useRef(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const pathname = usePathname();

    // High performance scroll tracking with Lenis
    useLenis((lenis) => {
        const isCurrentlyScrolled = lenis.scroll > 20;
        if (scrolledRef.current !== isCurrentlyScrolled) {
            scrolledRef.current = isCurrentlyScrolled;
            if (headerRef.current) {
                headerRef.current.setAttribute('data-scrolled', isCurrentlyScrolled.toString());
            }
        }
    });

    const openKoalendar = useCallback((e) => {
        e.preventDefault();
        window.Koalendar && window.Koalendar('open', {
            url: 'https://koalendar.com/e/rencontrer-contact-bizyoo'
        });
    }, []);

    // Handle Navbar link clicks
    const handleNavAction = useCallback((e, href) => {
        const linkId = href.split('#')[1];

        // Manual highlight on click
        if (linkId && pathname === '/') {
            setActiveSection(linkId);
        }

        handleAnchorClick(e, href);
        setMobileMenuOpen(false);
    }, [handleAnchorClick, pathname]);

    const navLinks = [
        { name: 'Accueil', href: '/#home' },
        { name: 'Nos Offres', href: '/#nos-offres' },
        { name: 'Nos Services', href: '/nos-services' },
        { name: 'Collectivités', href: '/#collectivites' },
        { name: 'Associations', href: '/#associations' },
        { name: 'Contact', href: '/contact' },
    ];

    return (
        <header ref={headerRef} className={styles.header}>
            <div className={styles.container}>
                {/* Logo */}
                <Link href="/" className={styles.logo}>
                    BIZYOO.
                </Link>

                {/* Desktop Nav */}
                <nav className={styles.desktopNav}>
                    {navLinks.map((link) => {
                        const linkId = link.href.split('#')[1];
                        // Link is active if it's the click-selected section OR the specific non-anchor path
                        const isActive = link.href.startsWith('/#')
                            ? (pathname === '/' && activeSection === linkId)
                            : pathname === link.href;

                        return (
                            <NavLink
                                key={link.name}
                                link={link}
                                isActive={isActive}
                                onClick={handleNavAction}
                                className={styles.navLink}
                            />
                        );
                    })}
                </nav>

                {/* Right CTA */}
                <div className={styles.rightActions}>
                    <Button
                        variant="primary"
                        size="sm"
                        href="/contact?diagnostic=1"
                        className={styles.desktopCta}
                        onClick={openKoalendar}
                    >
                        Diagnostic Gratuit
                    </Button>

                    {/* Mobile Toggle */}
                    <button
                        className={styles.mobileToggle}
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span className={`${styles.hamburger} ${mobileMenuOpen ? styles.open : ''}`}></span>
                    </button>
                </div>
            </div>

            {/* Mobile CTA — 2ème ligne centrée, visible uniquement sur mobile */}
            <div className={styles.mobileCta}>
                <Button
                    variant="primary"
                    size="sm"
                    href="/contact?diagnostic=1"
                    onClick={openKoalendar}
                >
                    Diagnostic Gratuit
                </Button>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className={styles.mobileMenu}>
                    <nav className={styles.mobileNav}>
                        {navLinks.map((link) => {
                            const linkId = link.href.split('#')[1];
                            const isActive = link.href.startsWith('/#')
                                ? (pathname === '/' && activeSection === linkId)
                                : pathname === link.href;

                            return (
                                <NavLink
                                    key={link.name}
                                    link={link}
                                    isActive={isActive}
                                    onClick={handleNavAction}
                                    className={styles.mobileNavLink}
                                />
                            );
                        })}
                    </nav>
                </div>
            )}

            <KoalendarWidget />
        </header>
    );
};